import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  PERMISSIONS,
  type AssetDto,
  type FundRequestDto,
  type Paginated,
  type Permission,
} from '@hrms/shared';
import { get } from '@/lib/api-client';

/** Below this a search matches half the register; above it, people mean it. */
export const MIN_RECORD_QUERY = 2;

/** Enough to recognise the one you meant, few enough not to bury the nav. */
const MAX_RECORDS = 5;

/**
 * `value`, but only after it has stopped changing for `ms`.
 *
 * The roster search matches in the browser, so it can run on every keystroke.
 * Assets cannot: each query is a request, and typing "EQ-0012" would fire eight
 * of them, seven of which are answered after they stopped being interesting.
 */
function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);

  return settled;
}

interface SourceOptions {
  open: boolean;
  query: string;
  canAny: (...permissions: Permission[]) => boolean;
}

/**
 * Assets matching the query, from the register's own search.
 *
 * Server-side because there is no small "all assets" list to cache the way the
 * staff roster is cached — a register runs to hundreds of rows and grows, and
 * the endpoint already searches tag, name, serial and brand, which is more than
 * a client-side match on a truncated page could do.
 */
export function useAssetMatches({ open, query, canAny }: SourceOptions) {
  const canSearch = canAny(PERMISSIONS.ASSET_READ_ALL, PERMISSIONS.ASSET_MANAGE);
  const search = useDebounced(query.trim(), 200);
  const enabled = open && canSearch && search.length >= MIN_RECORD_QUERY;

  const { data, isFetching } = useQuery({
    queryKey: ['assets', 'palette', search],
    queryFn: () =>
      get<Paginated<AssetDto>>('/assets', { params: { search, pageSize: MAX_RECORDS } }),
    enabled,
    staleTime: 60_000,
    // A failed lookup must not take the palette down with it; the nav still
    // works and that is what somebody hitting Cmd+K most often wants.
    throwOnError: false,
  });

  return {
    assets: canSearch && enabled ? (data?.data ?? []) : [],
    // Only "searching" while a query is actually in flight for the settled
    // text — not during the debounce, which would flicker on every keystroke.
    isSearching: enabled && isFetching,
  };
}

/**
 * Fund requests the caller can already see, matched in the browser.
 *
 * There is no search endpoint for these — the API offers "mine", the approver's
 * queue and the cashier's queue, and nothing that takes a term. Rather than
 * invent one, the palette searches the two lists this person is entitled to,
 * which is where "FR-0042" is nearly always looked for: your own request, or
 * the one waiting on your decision. A request belonging to somebody else, that
 * you are not approving, is not findable here — see the note in the palette.
 */
export function useFundRequestMatches({ open, query, canAny }: SourceOptions) {
  const canReadOwn = canAny(PERMISSIONS.FUND_REQUEST_READ_OWN);
  const canApprove = canAny(
    PERMISSIONS.FUND_REQUEST_APPROVE_MANAGER,
    PERMISSIONS.FUND_REQUEST_APPROVE_HR,
    PERMISSIONS.FUND_REQUEST_APPROVE_MANAGEMENT,
  );

  // The same keys the pages use, so whichever of them has been opened this
  // session leaves the palette nothing to fetch.
  const mine = useQuery({
    queryKey: ['finance', 'fund-requests', 'mine'],
    queryFn: () => get<FundRequestDto[]>('/finance/fund-requests/mine'),
    enabled: open && canReadOwn,
    staleTime: 60_000,
    throwOnError: false,
  });

  const awaiting = useQuery({
    queryKey: ['finance', 'fund-requests', 'awaiting'],
    queryFn: () => get<FundRequestDto[]>('/finance/fund-requests/awaiting-me'),
    enabled: open && canApprove,
    staleTime: 60_000,
    throwOnError: false,
  });

  const seen = new Set<string>();
  const requests: FundRequestDto[] = [];

  // Mine first, and deduplicated: a request can be both yours and waiting on
  // somebody at a higher rung, and the two queues would then offer it twice.
  for (const request of [...(mine.data ?? []), ...(awaiting.data ?? [])]) {
    if (seen.has(request.id)) continue;
    seen.add(request.id);
    requests.push(request);
  }

  return { requests, isSearching: mine.isFetching || awaiting.isFetching };
}
